import {useReconciliationRawTransactionsQuery} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import {Anchor, ChevronDown, ChevronRight, Flag, Plus} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import ActionHeader from 'src/components/ActionHeader';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {formatFullDate, formatListDate} from 'src/lib/displayDate';
import {cn} from 'src/lib/utils';
import {RawTransactionRow} from 'src/RawTransactionRow';
import {ReconcileMathSummary} from 'src/reconcile/ReconcileMathSummary';
import {
  inReconcileWindow,
  useReconcileSession,
} from 'src/reconcile/useReconcileSession';
import type {
  BalanceAnchorsQuery,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {DateTime} from 'luxon';

type Anchor = BalanceAnchorsQuery['balanceAnchors'][number];

export type ReconcileSession = ReturnType<typeof useReconcileSession>;

// The active session, shared via context between the anchor row that owns it
// (inline actions), the top action bar, and the summary footer. Null when no
// session runs. Declared above the row/header/footer that consume it.
export const ReconcileSessionContext =
  React.createContext<ReconcileSession | null>(null);

/**
 * A balance anchor interleaved into the transaction timeline: the date and
 * balance a reconciliation settled to (green, collapsible over the rows it
 * claimed), a balance still awaiting reconciliation (amber, reconcilable by
 * starting an in-place session), the baseline a span reconciles against
 * (neutral), or the starting balance the whole timeline stands on (sky).
 */
const ObservationRow = ({
  observation,
  isBaseline,
  isStartingBalance,
  currency,
  isCreditCard,
  expanded,
  onToggle,
  needed,
  sessionActive,
  onStartReconcile,
  onCancelReconcile,
  onFixUp,
}: {
  observation: Anchor;
  isBaseline: boolean;
  /**
   * Nothing on this account is dated before this anchor — it is where the
   * history starts, not an anchor waiting on a period to reconcile.
   */
  isStartingBalance: boolean;
  currency: string;
  isCreditCard: boolean;
  /** Only meaningful for reconciled (collapsible) anchors */
  expanded?: boolean;
  onToggle?: () => void;
  /** Balance delta an unreconciled anchor needs from its baseline */
  needed?: number;
  /** This anchor is the one being reconciled in the active session. */
  sessionActive?: boolean;
  onStartReconcile?: () => void;
  /** End the active session (only used on the session's own row). */
  onCancelReconcile?: () => void;
  /** Open the fix-up modal for the given signed remainder cents. */
  onFixUp?: (remainingCents: number) => void;
}) => {
  // The active session, so the owning row can carry the Fix up / Cancel /
  // Reconcile actions inline (mirrored by the pinned top bar). Null on every
  // other row and while idle.
  const session = React.useContext(ReconcileSessionContext);
  const formatMoney = useMoneyFormatter();
  const reconciled = Boolean(observation.reconciliation);
  // The starting balance outranks "Baseline": every anchor under a reconciled
  // span is a baseline, but only one is the floor the account is built on. It
  // can't outrank "Reconciled" — an anchor with a settled period of its own is
  // reporting that, not where it sits.
  const startingBalance = isStartingBalance && !reconciled;
  const label = reconciled
    ? 'Reconciled'
    : startingBalance
      ? 'Starting balance'
      : isBaseline
        ? 'Baseline'
        : 'Not reconciled';
  // Balances and their deltas render in the app-wide display convention:
  // credit-card anchors read as "amount owed" (toDisplayBalance), matching
  // the Overview tab, the observation detail page, and — in this tab — the
  // transaction rows themselves, which are also flipped to owed-space.
  const format = (cents: number) =>
    formatMoney(Money.fromInt(toDisplayBalance(cents, isCreditCard), currency));
  const claimedCount = observation.reconciliation?.rawTransactionIds.length;

  return (
    <div
      role={onToggle ? 'button' : undefined}
      onClick={onToggle}
      className={cn(
        // min-h-10 is the height this row has when it carries a button (h-6
        // plus py-2), held whether or not one is present. The buttons are
        // conditional in several directions at once — Reconcile appears only
        // on unreconciled anchors with a baseline, and swaps for Fix up /
        // Cancel / Reconcile once a session starts — so without a floor the
        // row would jump 8px as those states change, and reconciled anchors
        // would sit shorter than their unreconciled neighbours in the same
        // timeline.
        'bg-muted/50 flex min-h-10 items-center gap-x-5 px-4 py-2 text-xs',
        'not-last:border-border not-last:border-b',
        onToggle && 'hover:bg-muted cursor-pointer',
      )}
    >
      {/* Mirrors the transaction rows' leading checklist column so labels
          align with descriptions */}
      <span className="flex w-4 shrink-0 justify-center">
        {onToggle &&
          (expanded ? (
            <ChevronDown
              aria-hidden
              className="text-muted-foreground size-3.5"
            />
          ) : (
            <ChevronRight
              aria-hidden
              className="text-muted-foreground size-3.5"
            />
          ))}
      </span>
      <span
        className={cn(
          'flex items-center gap-1 font-medium',
          startingBalance
            ? 'text-sky-700 dark:text-sky-400'
            : reconciled || isBaseline
              ? 'text-muted-foreground'
              : 'text-amber-600 dark:text-amber-500',
        )}
        title={
          startingBalance
            ? 'The account starts here — nothing is dated before this balance.'
            : undefined
        }
      >
        {startingBalance && <Flag aria-hidden className="size-3" />}
        {label}
      </span>
      {reconciled && !expanded && (
        <span className="text-muted-foreground">
          {claimedCount} transaction{claimedCount === 1 ? '' : 's'}
        </span>
      )}
      <span className="ml-auto flex items-center gap-4">
        {/* The starting balance has no delta to state and nothing to ask for:
            it is not short a baseline, it *is* the baseline. */}
        {!reconciled && !startingBalance && (
          <span
            className={cn(
              'text-muted-foreground tabular-nums',
              // Desktop gives this space to the inline session actions. Mobile
              // swaps in only the primary Reconcile (same footprint as the
              // idle button), so the delta stays there — which is also what
              // keeps the row's height steady: at 390px the idle row wraps
              // around this text, and dropping it when a session starts
              // collapsed the row from 49px to the 40px floor, shifting the
              // whole timeline up by 9px. min-h alone can't hold a wrapped
              // height; keeping the content that causes the wrap can.
              sessionActive && 'md:hidden',
            )}
          >
            {needed === undefined
              ? 'needs an earlier baseline'
              : `needs ${format(needed)}`}
          </span>
        )}
        {!reconciled && !sessionActive && onStartReconcile && (
          <Button
            variant="outline"
            // touch-manipulation so a quick second tap (the mobile
            // double-tap-to-reconcile flow below) is never eaten by the
            // browser's double-tap zoom.
            className="h-6 touch-manipulation rounded px-2 py-0 text-xs"
            onClick={onStartReconcile}
            title="Reconcile this balance against the period's transactions"
          >
            Reconcile
          </Button>
        )}
        {sessionActive && (
          <>
            {/* Inline session actions, co-located with the row the user
                started from. Desktop carries the full trio; mobile is too
                narrow for three buttons, so the pinned top bar (always
                present) keeps Fix up / Cancel there and the row keeps just
                the primary Reconcile — in the same slot as the button that
                started the session, so a period that balances on its seeded
                selection reconciles with a quick double tap. */}
            {session && (
              <span className="hidden items-center gap-1.5 md:flex">
                <Button
                  variant="outline"
                  className="h-6 rounded px-2 py-0 text-xs"
                  disabled={!session.canFixUp}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFixUp?.(session.remaining);
                  }}
                  title="Create a transaction that offsets the remaining amount"
                >
                  Fix up
                </Button>
                <Button
                  variant="outline"
                  className="h-6 rounded px-2 py-0 text-xs"
                  disabled={session.submitting}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelReconcile?.();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="h-6 rounded px-2 py-0 text-xs"
                  disabled={!session.canSubmit}
                  onClick={(e) => {
                    e.stopPropagation();
                    void session.submit();
                  }}
                  title={
                    session.canSubmit
                      ? `Reconcile against ${session.selectedTransactions.length} selected transaction${
                          session.selectedTransactions.length === 1 ? '' : 's'
                        }`
                      : 'Adjust the selection until the expected balance matches'
                  }
                >
                  Reconcile
                </Button>
              </span>
            )}
            {session ? (
              <Button
                variant="primary"
                className="h-6 touch-manipulation rounded px-2 py-0 text-xs md:hidden"
                disabled={!session.canSubmit}
                onClick={(e) => {
                  e.stopPropagation();
                  void session.submit();
                }}
                title={
                  session.canSubmit
                    ? `Reconcile against ${session.selectedTransactions.length} selected transaction${
                        session.selectedTransactions.length === 1 ? '' : 's'
                      }`
                    : 'Adjust the selection until the expected balance matches'
                }
              >
                Reconcile
              </Button>
            ) : (
              <span className="font-medium text-blue-700 dark:text-blue-400">
                Reconciling…
              </span>
            )}
          </>
        )}
        <span className="text-muted-foreground tabular-nums">
          {format(observation.balance)}
        </span>
        <Link
          to={`/balance-anchors/${observation._id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-muted-foreground hover:text-foreground tabular-nums no-underline hover:underline"
          title="Open observation"
        >
          {formatListDate(observation.date)}
        </Link>
      </span>
    </div>
  );
};

/**
 * Per-row "record a balance dated here" affordance. Sparse accounts are
 * reconciled by dropping anchors (usually zero) at the points where the
 * balance is known, and typing the same date the row already shows is the
 * whole cost of that workflow — so the row offers it directly.
 *
 * Desktop only, deliberately. It reveals on hover, which touch has no
 * equivalent for, and a permanently visible target would have to be at least
 * 44px on a phone — the compact row spends ~236px of a 390px viewport on
 * fixed chrome already, and it sits between a tap that toggles selection and
 * a long press that navigates, so a small inline target invites mis-taps that
 * either write an anchor or discard a selection. The touch path is the
 * header's Record balance button, which prefills from the selected row.
 */
const RecordBalanceRowAction = ({
  date,
  onSelect,
}: {
  date: string;
  /**
   * Omitted when this date can't take an anchor, or while a session runs. The
   * slot still renders — empty — because the action is far more conditional
   * than it looks: it comes and goes as a session starts and ends, as anchors
   * load, and per row for claimed and unanchorable dates. Unmounting with it
   * would shift the amount and date columns of every affected row, so the
   * width is held unconditionally and only the button inside it changes.
   */
  onSelect?: (date: string) => void;
}) =>
  onSelect ? (
    <Button
      variant="ghost"
      // h-5 matches the row's tallest content (the text-sm description's line
      // box), so reserving a slot for the action doesn't make every row taller.
      // Only opacity toggles on hover — never presence — so running the cursor
      // down the list never reflows the rows either.
      className="hidden h-5 w-6 shrink-0 rounded p-0 opacity-0 shadow-none focus-visible:opacity-100 group-hover:opacity-100 md:flex"
      title={`Record a balance dated ${date}`}
      aria-label={`Record a balance dated ${date}`}
      // The row owns click (select) and long press (navigate); stop both here
      // so the action neither toggles the selection nor arms the press timer.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(date);
      }}
    >
      <Anchor aria-hidden className="text-muted-foreground size-3.5" />
    </Button>
  ) : (
    <span aria-hidden className="hidden h-5 w-6 shrink-0 md:block" />
  );

/**
 * Owns the reconcile workflow and shares the active session, via context, with
 * the summary and action bar that bracket the list. It stays mounted for the
 * whole life of the reconcile view — with a null anchor when no session runs —
 * so starting a session (clicking a row's Reconcile button) never remounts the
 * list and resets its scroll position. While a session is active it auto-pages
 * until the period window is fully loaded, seeds the selection with the
 * window's unreconciled rows, and exposes submit + the fix-up state; while idle
 * it supplies a null session and the summary/action bar render nothing.
 */
export const ReconcileSessionProvider = ({
  accountId,
  anchor,
  baseline,
  transactions,
  hasMoreTransactions,
  loadingMore,
  onLoadMore,
  selectedIds,
  onSeedSelection,
  onReconciled,
  children,
}: {
  accountId: string;
  /** The anchor being reconciled, or null when no session is running. */
  anchor: Anchor | null;
  /** Its baseline, or null when no session is running. */
  baseline: Anchor | null;
  transactions: BasicRawTransactionFragment[];
  hasMoreTransactions: boolean;
  loadingMore: boolean;
  onLoadMore?: () => void;
  selectedIds: ReadonlyArray<string>;
  onSeedSelection: (txns: BasicRawTransactionFragment[]) => void;
  onReconciled?: (claimedRawTransactionIds: string[]) => void;
  children: React.ReactNode;
}) => {
  const active = anchor != null && baseline != null;

  // Transactions arrive sorted newest-first, so the last one bounds the
  // loaded window; the span is covered once it reaches the baseline date.
  const oldestLoadedDate =
    transactions.length > 0
      ? transactions[transactions.length - 1].date
      : null;
  const windowCovered =
    !active ||
    !hasMoreTransactions ||
    (oldestLoadedDate != null &&
      baseline != null &&
      oldestLoadedDate <= baseline.date);

  // Auto-page until the period window is covered — the session needs the
  // full span before it can seed the selection or trust the sum. No-op while
  // idle so the reconcile list isn't force-paged without a session.
  React.useEffect(() => {
    if (!active || windowCovered || loadingMore || !onLoadMore) return;
    onLoadMore();
  }, [active, windowCovered, loadingMore, onLoadMore]);

  // The hook is called unconditionally (it must be — hooks can't be gated),
  // but `baselineResolved: active` keeps it from seeding or paging while idle,
  // and its result is only surfaced through context when a session runs.
  const session = useReconcileSession({
    accountId,
    anchorId: anchor?._id ?? '',
    anchorDate: anchor?.date ?? '',
    anchorBalance: anchor?.balance ?? 0,
    baselineDate: baseline?.date ?? null,
    baselineBalance: baseline?.balance ?? 0,
    baselineResolved: active,
    transactions,
    windowCovered,
    loadingTransactions: loadingMore,
    selectedIds,
    onSeedSelection,
    onReconciled,
  });

  return (
    <ReconcileSessionContext.Provider value={active ? session : null}>
      {children}
    </ReconcileSessionContext.Provider>
  );
};

/**
 * The session's actions, rendered in place of the account header while a
 * session runs. Built on the same ActionHeader as the idle header so the top
 * bar keeps the exact same height — starting a session swaps the buttons in
 * place instead of shifting the whole list down. "Fix up" opens a
 * create-transaction modal prefilled to offset the remainder and is disabled
 * once the period balances. Renders nothing when no session is active.
 */
export const ReconcileSessionHeader = ({
  anchor,
  onCancel,
  onFixUp,
}: {
  anchor: Anchor;
  onCancel: () => void;
  /**
   * Open the prefilled create-transaction modal to offset the remainder. Given
   * the signed remaining cents the adjustment should absorb.
   */
  onFixUp: (remainingCents: number) => void;
}) => {
  const session = React.useContext(ReconcileSessionContext);
  if (!session) return null;
  return (
    <ActionHeader
      content={
        <span className="text-sm font-medium whitespace-nowrap">
          Reconciling
          {/* The anchor date is redundant with the footer's "Recorded balance
              (date)" line, so it's dropped on mobile to keep the bar on one
              row next to the three buttons. */}
          <span className="text-muted-foreground ml-2 font-normal tabular-nums max-md:hidden">
            {formatListDate(anchor.date)}
          </span>
        </span>
      }
      // h-10 matches the idle header's icon buttons (size-5 glyph + p-2.5 =
      // 40px tall), so the row height is identical whether or not a session
      // is running.
      actions={
        <>
          <Button
            variant="outline"
            className="h-10 px-3 text-sm"
            disabled={!session.canFixUp}
            onClick={() => onFixUp(session.remaining)}
            title="Create a transaction that offsets the remaining amount"
          >
            Fix up
          </Button>
          <Button
            variant="outline"
            className="h-10 px-3 text-sm"
            disabled={session.submitting}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="h-10 px-4 text-sm"
            disabled={!session.canSubmit}
            onClick={() => void session.submit()}
            title={
              session.canSubmit
                ? `Reconcile against ${session.selectedTransactions.length} selected transaction${
                    session.selectedTransactions.length === 1 ? '' : 's'
                  }`
                : 'Adjust the selection until the expected balance matches'
            }
          >
            Reconcile
          </Button>
        </>
      }
    />
  );
};

/**
 * The running-math card, pinned below the list as the ScrollTable's footer so
 * the arithmetic stays in view — and in easy thumb reach on mobile — while the
 * user scrolls the period toggling rows. It slides up into place when a session
 * opens (the actions live in the top bar instead), and never overlaps the rows:
 * the list scrolls above it. Renders nothing when no session is active.
 */
export const ReconcileSessionSummaryFooter = ({
  anchor,
  baseline,
  currency,
  isCreditCard,
}: {
  anchor: Anchor;
  baseline: Anchor;
  currency: string;
  isCreditCard: boolean;
}) => {
  const session = React.useContext(ReconcileSessionContext);
  if (!session) return null;
  return (
    <div className="border-border bg-background animate-in slide-in-from-bottom-2 fade-in border-t p-3 duration-200">
      <ReconcileMathSummary
        baselineDate={baseline.date}
        baselineBalance={baseline.balance}
        recordedDate={anchor.date}
        needsStartingBalance={false}
        selectedSum={session.selectedSum}
        recordedBalance={anchor.balance}
        isBalanced={session.isBalanced}
        pending={session.summaryPending}
        isCreditCard={isCreditCard}
        currency={currency}
      />
      {session.summaryPending && (
        <p className="text-muted-foreground mt-1.5 text-xs">
          Loading all transactions in this period…
        </p>
      )}
      {session.error && (
        <p className="text-destructive mt-1.5 text-right text-xs">
          {session.error}
        </p>
      )}
    </div>
  );
};

/**
 * The claimed rows of an expanded reconciled anchor, fetched on demand. The
 * main list paginates unreconciled rows only, so a span's claimed history
 * isn't loaded until the user opens it — and then it's always the complete
 * set, never a page-bounded slice.
 */
const ExpandedReconciliationRows = ({
  anchorId,
  onLongPress,
  renderAmount,
}: {
  anchorId: string;
  onLongPress?: (transaction: BasicRawTransactionFragment) => void;
  renderAmount?: (
    transaction: BasicRawTransactionFragment,
  ) => React.ReactNode;
}) => {
  const {data, loading, error} = useReconciliationRawTransactionsQuery({
    variables: {anchorId},
  });
  const rows = React.useMemo(() => {
    const claimed = data?.balanceAnchor?.reconciliation?.rawTransactions ?? [];
    return [...claimed].sort(
      (a, b) => b.date.localeCompare(a.date) || b._id.localeCompare(a._id),
    );
  }, [data]);

  if (loading) {
    return (
      <div className="text-muted-foreground not-last:border-border px-4 py-2 text-xs not-last:border-b">
        Loading claimed transactions…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-destructive not-last:border-border px-4 py-2 text-xs not-last:border-b">
        {error.message}
      </div>
    );
  }
  return (
    <>
      {rows.map((transaction) => (
        <RawTransactionRow
          key={transaction._id}
          rawTransaction={transaction}
          onLongPress={() => onLongPress && onLongPress(transaction)}
          reconciliationChecklist
          compact
          renderAmount={renderAmount}
          // A claimed row can never take an anchor (its date is inside a
          // reconciled span), but it still holds the empty slot so its amount
          // and date line up with the unclaimed rows above it.
          rowAction={<RecordBalanceRowAction date={transaction.date} />}
        />
      ))}
    </>
  );
};

type Props = {
  /** Credit-card balances render as "amount owed" via toDisplayBalance */
  isCreditCard?: boolean;
  /**
   * The account's currency; falls back to the loaded transactions' currency
   * when absent. Matters for anchors on a page with no loaded transactions.
   */
  currency?: string;
  /**
   * The paginated UNRECONCILED rows (the owner queries with
   * reconciled: false). Claimed rows never appear here — each reconciled
   * span's rows load lazily when its anchor is expanded.
   */
  transactions: BasicRawTransactionFragment[];
  anchors: Anchor[];
  // While older unreconciled rows remain unloaded, observations below the
  // loaded window are hidden so a gap can't masquerade as clean history.
  hasMoreTransactions: boolean;
  selectedIds?: ReadonlyArray<string>;
  onClick?: (transaction: BasicRawTransactionFragment) => void;
  onLongPress?: (transaction: BasicRawTransactionFragment) => void;
  /**
   * The anchor whose reconcile session is running, and its baseline. The owner
   * holds this state (it also renders the session provider and footer bar), and
   * passes it down so the summary card and row highlighting stay in sync. Null
   * when no session is active.
   */
  activeAnchorId?: string | null;
  activeAnchor?: Anchor | null;
  activeBaseline?: Anchor | null;
  /** Start a session on the given (unreconciled, baselined) anchor. */
  onStartReconcile?: (anchorId: string) => void;
  /**
   * Anchors that have just been reconciled but whose refreshed state hasn't
   * arrived yet. They still read as unreconciled here, so they must not offer
   * to start again — the server rejects a second reconciliation of the same
   * anchor.
   */
  completedAnchorIds?: ReadonlySet<string>;
  /** Cancel the active session — wired to the session row's inline Cancel. */
  onCancelReconcile?: () => void;
  /** Open the fix-up modal for the given signed remainder — inline Fix up. */
  onFixUp?: (remainingCents: number) => void;
  /**
   * Whether the balance observations have finished loading. The bootstrap
   * affordance keys off the absence of an earlier observation, so it must wait
   * for this — an empty-because-pending anchor list would wrongly offer to
   * bootstrap an account that already has a baseline.
   */
  anchorsLoaded?: boolean;
  /**
   * Whether a synced Plaid reading predates the earliest anchor. Readings
   * aren't reconcilable and never appear in this timeline, but they are
   * attested balances the Overview chart draws — so an anchor with one below
   * it is not where the account's history starts. Resolved by the owner (only
   * Plaid-linked accounts can have readings at all).
   */
  hasReadingBeforeAnchors?: boolean;
  /**
   * Open the record-balance modal to seed a starting balance, prefilled to the
   * given date. Offered at the end of the loaded timeline when no observation
   * predates the earliest transaction, so an old account can be bootstrapped
   * into a reconcilable state.
   */
  onAddStartingBalance?: (initialDate: DateTime) => void;
  /**
   * Open the record-balance modal anchored to a transaction row's own date —
   * the desktop hover action on each row. Also suppressed while a reconcile
   * session is running, when row clicks belong to the session.
   */
  onRecordBalanceAt?: (date: string) => void;
  /**
   * Whether a date can take a new anchor. The owner resolves this against the
   * loaded anchors and reconciled spans (see canRecordBalanceOn there) so the
   * row action is offered only where the server would accept the write —
   * rather than this view re-deriving the rule from `anchors`, which would
   * drift from the header button's copy of it.
   */
  canRecordBalanceOn?: (date: string) => boolean;
  /**
   * Rows a just-finished reconciliation claimed, still mounted while they
   * animate out. The owner drops them from the cache once the animation ends
   * (ROW_EXIT_MS there matches the row's transition duration).
   */
  leavingIds?: ReadonlyArray<string>;
};

type TimelineItem =
  | {kind: 'observation'; seconds: number; observation: Anchor}
  | {
      kind: 'transaction';
      seconds: number;
      transaction: BasicRawTransactionFragment;
    };

/**
 * The Reconcile view's body: unreconciled raw transactions and balance
 * observations merged into one descending timeline. Reconciled spans render
 * as their anchor row alone (claimed rows load lazily on expansion), so
 * pagination is spent entirely on unreconciled rows — the ones reconcile
 * work actually cares about. Stragglers passed over by a past reconciliation
 * therefore surface within the first pages instead of behind a statement's
 * worth of load-more clicks.
 *
 * Unreconciled anchors reconcile through a session owned by the parent (via
 * ReconcileSessionProvider + useReconcileSession): starting one auto-loads the
 * period, seeds the selection with its unreconciled rows, pins the running math
 * to the top of the timeline, and drives the footer action bar.
 */
export const ReconcileTimeline = ({
  isCreditCard = false,
  currency: currencyProp,
  transactions,
  anchors,
  hasMoreTransactions,
  selectedIds = [],
  onClick,
  onLongPress,
  activeAnchorId = null,
  activeAnchor = null,
  activeBaseline = null,
  onStartReconcile,
  completedAnchorIds,
  onCancelReconcile,
  onFixUp,
  anchorsLoaded = false,
  hasReadingBeforeAnchors = false,
  onAddStartingBalance,
  onRecordBalanceAt,
  canRecordBalanceOn,
  leavingIds,
}: Props) => {
  const currency = currencyProp ?? transactions[0]?.currency ?? 'USD';
  const selectedIdSet = React.useMemo(
    () => new Set(selectedIds),
    [selectedIds],
  );
  const leavingIdSet = React.useMemo(
    () => new Set(leavingIds ?? []),
    [leavingIds],
  );
  const [expandedIds, setExpandedIds] = React.useState<ReadonlySet<string>>(
    new Set(),
  );

  const toggleExpanded = (id: string) =>
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // Anchors sorted newest-first; each unreconciled anchor's baseline is
  // the nearest anchor below it, mirroring how the server resolves baselines.
  const sortedObservations = React.useMemo(
    () => [...anchors].sort((a, b) => b.date.localeCompare(a.date)),
    [anchors],
  );
  const baselineFor = React.useMemo(() => {
    const map = new Map<string, Anchor | undefined>();
    sortedObservations.forEach((observation, index) => {
      map.set(observation._id, sortedObservations[index + 1]);
    });
    return map;
  }, [sortedObservations]);

  // The session window, for marking boundary rows. Null when no session.
  const sessionWindow =
    activeAnchor && activeBaseline
      ? {baselineDate: activeBaseline.date, anchorDate: activeAnchor.date}
      : null;

  // Observations referenced as a reconciliation's baseline are labeled
  // neutrally (e.g. a starting balance); unreconciled ones can still be
  // reconciled to backfill their own earlier period.
  const baselineIds = React.useMemo(
    () =>
      new Set(
        anchors.flatMap((a) =>
          a.reconciliation?.baselineAnchor
            ? [a.reconciliation.baselineAnchor._id]
            : [],
        ),
      ),
    [anchors],
  );

  // Unreconciled transactions dated inside a reconciled span (baseline.date,
  // observation.date] were passed over by that reconciliation — flag them as
  // discrepancies, matching the server's needsReview span semantics. Rows
  // outside every span (including gaps before an unreconciled baseline that
  // no reconciliation has covered) simply haven't been reached and stay
  // quiet.
  const reconciledSpans = React.useMemo(() => {
    const spans: Array<{start: number; end: number}> = [];
    for (const a of anchors) {
      // The span is stored on the reconciliation itself.
      if (!a.reconciliation) continue;
      spans.push({
        start: parseDate(a.reconciliation.startDate).toSeconds(),
        end: parseDate(a.reconciliation.endDate).toSeconds(),
      });
    }
    return spans;
  }, [anchors]);
  const isMissed = (seconds: number) =>
    reconciledSpans.some((span) => seconds > span.start && seconds <= span.end);

  const items: TimelineItem[] = React.useMemo(() => {
    const transactionItems = transactions.map((transaction) => ({
      kind: 'transaction' as const,
      seconds: parseDate(transaction.date).toSeconds(),
      transaction,
    }));
    // Transactions arrive sorted newest-first, so the last one bounds the
    // loaded window.
    const oldestLoaded =
      transactionItems.length > 0
        ? transactionItems[transactionItems.length - 1].seconds
        : 0;
    const observationItems = sortedObservations
      .map((observation) => ({
        kind: 'observation' as const,
        seconds: parseDate(observation.date).toSeconds(),
        observation,
      }))
      .filter((item) => !hasMoreTransactions || item.seconds >= oldestLoaded);
    // Descending by date; on ties the anchor sits above the transactions it
    // covers (a reconciliation includes its ending date).
    return [...observationItems, ...transactionItems].sort(
      (a, b) =>
        b.seconds - a.seconds ||
        (a.kind === b.kind ? 0 : a.kind === 'observation' ? -1 : 1),
    );
  }, [transactions, sortedObservations, hasMoreTransactions]);

  // In this tab, credit-card row amounts flip to "amount owed" space
  // (charges positive), matching the anchor balances and the session math so
  // the timeline reads like a statement. Rendered plain — the green
  // income-coloring of the cash-flow convention would invert meaning here.
  const renderOwedAmount = isCreditCard
    ? (rt: BasicRawTransactionFragment) => (
        <AmountDisplay
          amount={Money.fromInt(toDisplayBalance(rt.amount, true), rt.currency)}
          variant="plain"
        />
      )
    : undefined;

  // Rows arrive newest-first, so the last one is the earliest loaded
  // transaction. Once the whole timeline is loaded and no observation predates
  // it, the earliest period has no baseline to reconcile against — offer to
  // seed a starting balance dated the day before, which bootstraps an old
  // account into a reconcilable state.
  const oldestTransaction =
    transactions.length > 0 ? transactions[transactions.length - 1] : null;
  const hasEarlierObservation =
    oldestTransaction != null &&
    anchors.some((anchor) => anchor.date < oldestTransaction.date);
  const canAddStartingBalance =
    anchorsLoaded &&
    !hasMoreTransactions &&
    oldestTransaction != null &&
    !hasEarlierObservation &&
    onAddStartingBalance != null;

  // The account's earliest anchor is its starting balance once nothing is
  // dated on or before it — the exact complement of the bootstrap affordance
  // above, so a timeline shows one or the other and never both. Note "on or
  // before": a row sharing the anchor's own day can never be reconciled from
  // here (a span covers (baseline.date, ending.date]), so that anchor still
  // wants an earlier baseline. The server draws the same boundary, so this
  // tab and the Overview chart can't disagree about the same anchor.
  //
  // It takes the whole list being loaded to know: while older rows remain
  // unfetched, an earlier transaction may still turn up, and the anchor is
  // genuinely short a baseline until then. (Rows before the earliest anchor
  // can only be unreconciled — a reconciled one would sit inside a span whose
  // baseline is earlier still — so the paginated unreconciled list is enough
  // to decide.)
  const earliestAnchor =
    sortedObservations.length > 0
      ? sortedObservations[sortedObservations.length - 1]
      : null;
  const startingBalanceId =
    earliestAnchor != null &&
    !hasMoreTransactions &&
    !hasReadingBeforeAnchors &&
    (oldestTransaction == null || earliestAnchor.date < oldestTransaction.date)
      ? earliestAnchor._id
      : null;

  const body = (
    <div>
      {items.map((item) => {
        if (item.kind === 'observation') {
          const {observation} = item;
          const reconciled = Boolean(observation.reconciliation);
          const isBaseline = baselineIds.has(observation._id);
          const baseline = baselineFor.get(observation._id);
          // An unreconciled baseline can itself be reconciled later
          // (backfilling an earlier period without disturbing the span that
          // references it) — but only when an earlier anchor exists; a true
          // starting balance stays quiet instead of asking for a baseline it
          // will never have.
          const reconcilable =
            !reconciled && (!isBaseline || baseline !== undefined);
          const expanded = expandedIds.has(observation._id);
          return (
            <React.Fragment key={`observation-${observation._id}`}>
              <ObservationRow
                observation={observation}
                isBaseline={isBaseline}
                isStartingBalance={observation._id === startingBalanceId}
                currency={currency}
                isCreditCard={isCreditCard}
                expanded={expanded}
                onToggle={
                  reconciled ? () => toggleExpanded(observation._id) : undefined
                }
                needed={
                  baseline ? observation.balance - baseline.balance : undefined
                }
                sessionActive={observation._id === activeAnchorId}
                onStartReconcile={
                  reconcilable &&
                  baseline &&
                  onStartReconcile &&
                  !completedAnchorIds?.has(observation._id)
                    ? () => onStartReconcile(observation._id)
                    : undefined
                }
                onCancelReconcile={onCancelReconcile}
                onFixUp={onFixUp}
              />
              {reconciled && expanded && (
                <ExpandedReconciliationRows
                  anchorId={observation._id}
                  onLongPress={onLongPress}
                  renderAmount={renderOwedAmount}
                />
              )}
            </React.Fragment>
          );
        }
        return (
          <RawTransactionRow
            key={item.transaction._id}
            rawTransaction={item.transaction}
            selected={selectedIdSet.has(item.transaction._id)}
            onClick={onClick ? () => onClick(item.transaction) : undefined}
            onLongPress={() => onLongPress && onLongPress(item.transaction)}
            reconciliationChecklist
            compact
            // A leaving row was just claimed, so it can never be a
            // discrepancy — but for the length of the exit it looks like one
            // from the outside: the anchor refetch lands the new span while
            // the row is still cached with reconciliation == null, which is
            // exactly the shape isMissed reports on. Without this the success
            // animation flashes a red "missed by reconciliation" mark on the
            // rows the reconciliation just succeeded in claiming.
            missedReconciliation={
              !item.transaction.reconciliation &&
              !leavingIdSet.has(item.transaction._id) &&
              isMissed(item.seconds)
            }
            outsidePeriod={
              sessionWindow != null &&
              !item.transaction.reconciliation &&
              !inReconcileWindow(sessionWindow, item.transaction.date)
            }
            renderAmount={renderOwedAmount}
            // Always a boolean, never undefined: max-height only animates from
            // a resolved length, so a row that picks up its bounds in the same
            // render that starts the exit would snap shut instead of
            // collapsing. Every row in this list is armed from the start.
            leaving={leavingIdSet.has(item.transaction._id)}
            rowAction={
              <RecordBalanceRowAction
                date={item.transaction.date}
                onSelect={
                  activeAnchorId == null &&
                  canRecordBalanceOn?.(item.transaction.date)
                    ? onRecordBalanceAt
                    : undefined
                }
              />
            }
          />
        );
      })}
      {canAddStartingBalance && oldestTransaction && onAddStartingBalance && (
        <div className="bg-muted/30 flex items-center gap-x-5 px-4 py-3 text-xs">
          {/* Aligns with the rows' leading checklist column */}
          <span className="w-4 shrink-0" />
          <span className="text-muted-foreground">
            No balance recorded before{' '}
            {formatFullDate(oldestTransaction.date)} — the earliest
            transactions have no baseline to reconcile against.
          </span>
          <Button
            variant="outline"
            className="ml-auto h-7 shrink-0 rounded px-2.5 py-0 text-xs"
            onClick={() =>
              onAddStartingBalance(
                parseDate(oldestTransaction.date).minus({days: 1}),
              )
            }
            title="Record a starting balance dated the day before the earliest transaction"
          >
            <Plus className="mr-1 size-3.5" />
            Add starting balance
          </Button>
        </div>
      )}
    </div>
  );

  // The owner mounts the provider around this timeline (plus the session's top
  // action bar and the summary footer, which read the session from context).
  return body;
};
